import React, { useState, useRef, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

// Shared month picker (prev/next arrows + click-to-open year/month grid) — used by
// ViewLogbooks.js and DeanDashboard.js. Reuses the `.vl-*` classes from ViewLogbooks.css,
// which already have mobile breakpoints tuned for this exact widget.
// `value` is a 'YYYY-MM' string; `onChange(newValue)` fires when the month changes.
const MonthPicker = ({ value, onChange }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(() => parseInt(value.split('-')[0], 10));
  const pickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowPicker(false);
      }
    };
    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showPicker]);

  const getMonthDisplayName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month, 10) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const goToPreviousMonth = () => {
    const [y, m] = value.split('-').map(Number);
    const d = new Date(y, m - 2);
    onChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const goToNextMonth = () => {
    const [y, m] = value.split('-').map(Number);
    const d = new Date(y, m);
    const now = new Date();
    const maxMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const newMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (newMonth <= maxMonth) {
      onChange(newMonth);
    }
  };

  return (
    <div className="filter-item" ref={pickerRef}>
      <label>Month</label>
      <div className="vl-month-nav">
        <button className="vl-nav-arrow" onClick={goToPreviousMonth} title="Previous month">
          <FaChevronLeft />
        </button>
        <button
          className="vl-month-label-clickable"
          onClick={() => {
            setPickerYear(parseInt(value.split('-')[0], 10));
            setShowPicker(!showPicker);
          }}
        >
          {getMonthDisplayName(value)}
        </button>
        <button className="vl-nav-arrow" onClick={goToNextMonth} title="Next month">
          <FaChevronRight />
        </button>
      </div>
      {showPicker && (
        <div className="vl-month-picker">
          <div className="vl-picker-year-nav">
            <button className="vl-picker-arrow" onClick={() => setPickerYear(prev => prev - 1)}>
              <FaChevronLeft />
            </button>
            <span className="vl-picker-year">{pickerYear}</span>
            <button
              className="vl-picker-arrow"
              onClick={() => setPickerYear(prev => Math.min(prev + 1, new Date().getFullYear()))}
            >
              <FaChevronRight />
            </button>
          </div>
          <div className="vl-picker-months">
            {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => {
              const monthVal = `${pickerYear}-${String(i + 1).padStart(2, '0')}`;
              const now = new Date();
              const maxMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
              const isFuture = monthVal > maxMonth;
              return (
                <button
                  key={m}
                  className={`vl-picker-month${monthVal === value ? ' selected' : ''}${isFuture ? ' disabled' : ''}`}
                  disabled={isFuture}
                  onClick={() => {
                    onChange(monthVal);
                    setShowPicker(false);
                  }}
                >
                  {m}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthPicker;
